import type { ComputedRef, InjectionKey } from 'vue'
import { computed, readonly, ref } from 'vue'

/**
 * Z-index of the FIRST overlay (slideover or modal) backdrop. Panel sits at
 * `OVERLAY_Z_BASE + OVERLAY_Z_PANEL_OFFSET`. Each additional nested overlay
 * shifts up by `OVERLAY_Z_LAYER_STEP`, so the second backdrop is 2020, its
 * panel 2030, etc.
 *
 * Kept in sync with the `--z-index-overlay-base` / `--z-index-panel-base`
 * tokens in `src/styles/theme.css`.
 */
export const OVERLAY_Z_BASE = 2000
export const OVERLAY_Z_LAYER_STEP = 20
export const OVERLAY_Z_PANEL_OFFSET = 10

/**
 * Single global stack of currently-open overlays. Order = open order
 * (oldest first). An overlay's index in the stack determines its z-index.
 */
const stack = ref<symbol[]>([])

/**
 * Provide / inject key used internally by Sheet primitive to share the
 * backdrop z-index between sibling SheetContent and SheetOverlay. Dialog
 * renders both elements in one file and doesn't need it.
 */
export const OVERLAY_BACKDROP_Z_KEY: InjectionKey<ComputedRef<number>> = Symbol('overlay-backdrop-z')

/**
 * Panel z-index of the topmost (most recently opened) overlay, or `null`
 * when no overlay is open.
 *
 * This is the global signal that floating UI (Select, Combobox, Popover,
 * Tooltip, HoverCard) listens to. Provide/inject does NOT work for that
 * use case because Select etc. are typically passed as SLOT content inside
 * modals — and in Vue 3 slot content inherits provides from its scope owner
 * (the feature component), not from the component that renders `<slot>`
 * (DialogContent). Using a global ref bypasses that limitation entirely.
 */
export const topmostOverlayPanelZ: ComputedRef<number | null> = computed(() => {
  const depth = stack.value.length
  if (depth === 0) {
    return null
  }
  return OVERLAY_Z_BASE + (depth - 1) * OVERLAY_Z_LAYER_STEP + OVERLAY_Z_PANEL_OFFSET
})

export interface OverlayStackSlot {
  id: symbol
  backdropZ: ComputedRef<number>
  panelZ: ComputedRef<number>
  release: () => void
}

/**
 * Acquire a slot in the global overlay stack. Each open Sheet/Dialog must
 * call this once on open and call `release()` on close (typically wired via
 * a watcher on the open state).
 *
 * Returns reactive z-indexes that automatically decrement if overlays opened
 * after this one are closed first.
 *
 * @example
 *   const { backdropZ, panelZ, release } = useOverlayStack().acquire()
 *   // <div :style="{ zIndex: backdropZ }" />
 *   // <div :style="{ zIndex: panelZ }" />
 *   // onClose: release()
 */
export function useOverlayStack() {
  function acquire(): OverlayStackSlot {
    const id = Symbol('overlay-stack-slot')
    stack.value.push(id)

    const index = computed(() => {
      const i = stack.value.indexOf(id)
      // After release the slot is gone; fall back to base so any in-flight
      // closing animation still renders sensibly.
      return i === -1 ? 0 : i
    })

    const backdropZ = computed(() => OVERLAY_Z_BASE + index.value * OVERLAY_Z_LAYER_STEP)
    const panelZ = computed(() => backdropZ.value + OVERLAY_Z_PANEL_OFFSET)

    function release() {
      const i = stack.value.indexOf(id)
      if (i !== -1) {
        stack.value.splice(i, 1)
      }
    }

    return { id, backdropZ, panelZ, release }
  }

  return { acquire, stack: readonly(stack), topmostPanelZ: topmostOverlayPanelZ }
}
